import { useLayoutEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import type { ActionItem, CustomerOverview, Unternehmen } from "@kundeninfo/api-client";
import { Chip, MeObjektliste, MeObjektzeile, Objektsymbol, statusRang } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { keepInView } from "../../scroll.js";
import { COV_TEXT, actionHeading, actionRowView, incompleteText, showAllText, soonText, truncatedText, urgentText }
  from "./texts.js";

/*
 * Die Leiste „Handlungsbedarf“ oben in der Übersicht (373, 382, 418): was über alle Unternehmen des Benutzers Aufmerksamkeit
 * braucht, ein Sprung je Zeile. Sie folgt weder der Sicht noch lässt sie sich ausblenden.
 *
 * Nie falsch grün: „Kein Handlungsbedarf“ steht nur, wenn die Antwort da ist UND jede Quelle geprüft wurde. Lädt die Leiste noch
 * oder ist der Abruf gescheitert, sagt sie das; fehlt eine Quelle, steht „unvollständig“ neben dem, was vorliegt.
 */

export type ActionBarState =
  | { kind: "loading" }
  | { kind: "failed"; reloading?: true }
  | { kind: "ready"; today: string; actionItems: CustomerOverview["actionItems"]; missingSources: CustomerOverview["missingSources"] };

/** So viele Zeilen zeigt die Leiste eingeklappt. */
export const ACTION_ROWS_COLLAPSED = 5;

export type ActionBarProps = {
  state: ActionBarState;
  /** Die Unternehmen, über die Kennung, Kürzel, Name und Hausfarbe einer Zeile gelesen werden. */
  companies: readonly Unternehmen[];
  /** Das Kürzel an jeder Zeile; nur bei Kunden mit mehreren Unternehmensbeziehungen nötig (wie an den Kacheln). */
  showCompanyCodes?: boolean;
  /** Steht rechts in der Titelzeile neben der Überschrift (nicht in ihr): bei der Übersicht der Knopf „Kacheln anpassen“. */
  headerAction?: ReactNode;
  onItem: (item: ActionItem) => void;
  onRetry: () => void;
};

const TITLE_ID = "cov-bedarf-titel";
const NO_COUNTS = { error: 0, warn: 0 };

/** Ein Kennzeichen im Kopf; `counted` heißt: Die Zahl steht schon in `actionHeading`. */
type Mark = { art: "fehler" | "unbekannt" | "warnung" | "ok"; text: string; counted?: true };

/**
 * Die Zähler, mindestens so groß wie die gelieferten Einträge je Stufe: Ein widersprüchlicher Stand (Zähler kleiner als
 * die Liste) wird nie grün und lässt keine Zeile unerreichbar. Der Server liefert beides aus derselben Menge.
 */
function countsOf(a: CustomerOverview["actionItems"]): { error: number; warn: number } {
  const listed = (level: ActionItem["level"]): number => a.items.filter((i) => i.level === level).length;
  return { error: Math.max(a.counts.error, listed("ERROR")), warn: Math.max(a.counts.warn, listed("WARN")) };
}

/** Die Kennzeichen im Kopf, Schlechtestes zuerst (Hausregel „Listen: Schlechtestes oben“). */
function marksOf(state: ActionBarState): Mark[] {
  if (state.kind === "loading") return [];
  if (state.kind === "failed") return [{ art: "unbekannt", text: COV_TEXT.actionFailed }];
  const { complete } = state.actionItems;
  const counts = countsOf(state.actionItems);
  const marks: Mark[] = [];
  if (counts.error > 0) marks.push({ art: "fehler", text: urgentText(counts.error), counted: true });
  if (counts.warn > 0) marks.push({ art: "warnung", text: soonText(counts.warn), counted: true });
  // `complete` allein genügt nicht: Fehlt laut Liste eine Handlungsquelle, ist die Leiste unvollständig (der Ansprechpartner zählt nicht).
  if (!complete || state.missingSources.some((source) => source !== "CONTACT")) {
    marks.push({ art: "unbekannt", text: incompleteText(state.missingSources) });
  }
  return marks.length === 0 ? [{ art: "ok", text: COV_TEXT.actionEmpty }]
    : marks.toSorted((a, b) => statusRang(a.art) - statusRang(b.art));
}

/**
 * Schlüssel einer Zeile: Art, Ziel (mit der Kennung) und Tag, nie die Kennung allein — Posten und Entwurf desselben Auftrags teilen
 * sie, und Verträge haben keine (`reference` ""): zwei überfällige Posten desselben Vertrags unterscheidet erst der Tag.
 */
const rowKey = (item: ActionItem): string => `${item.kind}|${item.reference}|${JSON.stringify(item.target)}|${item.date}`;

/** Die Schlüssel der gezeigten Zeilen; eine Zeile, die auch das nicht unterscheidet, bekommt ihre Folgenummer (nie doppelt). */
function rowKeys(items: readonly ActionItem[]): string[] {
  const seen = new Map<string, number>();
  return items.map((item) => {
    const key = rowKey(item);
    const n = seen.get(key) ?? 0;
    seen.set(key, n + 1);
    return n === 0 ? key : `${key}#${n}`;
  });
}

export function ActionBar(p: ActionBarProps): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const toggle = useRef<HTMLElement | null>(null);
  const restore = useRef<(() => void) | null>(null);
  // Beim Zuklappen rücken die Zeilen über dem Knopf weg; er bleibt dort im rollenden Bereich, wo er war (Safari verankert nicht
  // selbst, und in der Anwendung rollt ein innerer Container). Beim Aufklappen wächst die Leiste nach unten, ohne dass etwas rollt
  // (Hausregel „Scroll bei Neuzeichnen“).
  useLayoutEffect(() => {
    restore.current?.();
    restore.current = null;
  }, [expanded]);
  const flip = (): void => {
    if (expanded && toggle.current !== null) restore.current = keepInView(toggle.current);
    setExpanded(!expanded);
  };

  const ready = p.state.kind === "ready" ? p.state : null;
  const items = ready?.actionItems.items ?? [];
  const counts = ready === null ? NO_COUNTS : countsOf(ready.actionItems);
  const total = counts.error + counts.warn;
  // Fällt die Zahl unter die Schwelle (neuer Abruf), gibt es nichts mehr aufzuklappen; ein späterer Anstieg beginnt zugeklappt.
  if (expanded && total <= ACTION_ROWS_COLLAPSED) setExpanded(false);
  const shown = expanded ? items : items.slice(0, ACTION_ROWS_COLLAPSED);
  const keys = rowKeys(shown);
  const marks = marksOf(p.state);
  const name = [actionHeading(counts), ...marks.filter((m) => m.counted !== true).map((m) => m.text)].join(", ");
  const codes = p.showCompanyCodes ?? true;

  return (
    <section className="cov__bedarf" aria-labelledby={TITLE_ID}>
      <div className="cov__bedarf-kopf">
        <h2 id={TITLE_ID} className="cov__bedarf-titel" aria-label={name}>
          {COV_TEXT.actionTitle}
          {marks.map((m) => <Chip key={m.art} statusArt={m.art}>{m.text}</Chip>)}
        </h2>
        {p.headerAction}
      </div>
      {p.state.kind === "loading" && <p className="cov__bedarf-hinweis">{COV_TEXT.loading}</p>}
      {p.state.kind === "failed" && <KiKnopf onClick={p.onRetry}>{COV_TEXT.retry}</KiKnopf>}
      {/* Der neue Abruf nach einem Fehlschlag: Der Knopf bleibt bedienbar (der Fokus bleibt auf ihm), darunter steht, dass es läuft. */}
      {p.state.kind === "failed" && p.state.reloading === true && <p className="cov__bedarf-hinweis">{COV_TEXT.loading}</p>}
      {ready !== null && shown.length > 0 && (
        <MeObjektliste aria-label={COV_TEXT.actionTitle}>
          {shown.map((item, index) => {
            const view = actionRowView(item, ready.today, p.companies);
            const state = view.status === undefined || view.statusArt === undefined ? {}
              : { status: view.status, statusArt: view.statusArt };
            const tint = view.companyColor === undefined ? {} : { farbe: view.companyColor };
            return (
              // `umbruch`: Name in höchstens zwei Zeilen — am Telefon bliebe von „Fälligkeit verpasst, …“ sonst „Fälligkeit v…“.
              <MeObjektzeile key={keys[index]} name={view.name} kennung={view.kennung} meta={view.meta} {...state} umbruch
                aria-label={view.ariaLabel} icon={<Objektsymbol art={view.art} />}
                {...(codes ? { chips: <Chip {...tint}>{view.companyCode}</Chip> } : {})}
                onGewaehlt={() => p.onItem(item)} />
            );
          })}
        </MeObjektliste>
      )}
      {total > ACTION_ROWS_COLLAPSED && (
        <KiKnopf art="leise" knopfRef={toggle} onClick={flip}>{expanded ? COV_TEXT.showLess : showAllText(total)}</KiKnopf>
      )}
      {expanded && items.length < total && <p className="cov__bedarf-hinweis">{truncatedText(items.length, total)}</p>}
    </section>
  );
}
